import { CONTROL_TOOLTIPS } from '@src/containers/Blotters/tooltips';
import { useIncludeCashFilterFunc } from '@src/hooks/useIncludeCashFilter';
import {
  ACTION,
  BLOTTER_TABLE_FILTERS_CONTAINER_ID,
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  Button,
  ButtonVariants,
  DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
  Divider,
  EXTRAS_MENU_TOOLTIP_PROPS,
  ExportMenuItems,
  FormControlSizes,
  IconButton,
  IconName,
  MixpanelEvent,
  MixpanelEventProperty,
  Toggle,
  removeEmptyFilters,
  useAccordionFilterBuilder,
  useBlotterGroupingToggling,
  useBlotterTableExtrasMenu,
  useCallbackRef,
  useConstant,
  useMixpanel,
  usePersistedBlotterTable,
  useWSFilterPipe,
  useWsBlotterTable,
  type Position,
  type WebsocketRequest,
} from '@talos/kyoko';
import type { StateUpdatedEvent } from 'ag-grid-community';
import { useMemo } from 'react';
import { useFeatureFlag, useRoleAuth } from '../../../../hooks';
import { defaultSubAccountsBlotterColumns, defaultSubAccountsBlotterFilter } from '../defaults';
import type { ClonePositionsTabFunc, SubAccountPositionsTabProps } from '../types';
import { usePositionsMenu } from '../usePositionsMenu';
import { SubAccountTreeWarning } from './SubAccountTreeWarning';
import { treeBlotterContextMenuColumnResolver } from './treeBlotterContextMenuColumnResolver';
import { useHierarchicalVariantSpecs } from './useHierarchicalVariantOptions';
import { usePositionsSubAccountsColumns } from './usePositionsSubAccountsColumns';
import { usePositionsSubAccountsFilter } from './usePositionsSubAccountsFilter';
import { useRowGroupVariantSpecs } from './useRowGroupVariantOptions';
import { useSubAccountPositionsBlotterRequest } from './useSubAccountPositionsBlotterRequest';

export interface PositionsSubAccountsBlotterParams {
  blotterID: string;
  initialIsOpen?: boolean;
  tab: SubAccountPositionsTabProps;
  onUpdateTab: (tab: SubAccountPositionsTabProps) => void;
  onCloneTab: ClonePositionsTabFunc;
  tag?: string;
}

// Small wrapper around the meat of the component to remount it when the hierarchical setting changes.
export function PositionsSubAccountsBlotter(props: PositionsSubAccountsBlotterParams) {
  return <PositionsSubAccountsBlotterInner key={`${!!props.tab.hierarchical}`} {...props} />;
}

function getPositionKey(position: Position) {
  return position.rowID;
}

function getPositionAsset(position: Position) {
  return position.Asset;
}

function PositionsSubAccountsBlotterInner({
  blotterID,
  initialIsOpen,
  tab,
  onUpdateTab,
  onCloneTab,
  tag,
}: PositionsSubAccountsBlotterParams) {
  const mixpanel = useMixpanel();
  const { isAuthorized } = useRoleAuth();
  const isAuthorizedToEditPositions = isAuthorized(ACTION.EDIT_POSITION);

  const { enableHierarchicalSubAccountPositionsBlotter } = useFeatureFlag();

  const defaultColumns = tab.defaultColumns ?? defaultSubAccountsBlotterColumns;
  const defaultFilter = tab.defaultFilter ?? defaultSubAccountsBlotterFilter;
  const showZeroBalances = tab.showZeroBalances ?? false;
  const hierarchical = (tab.hierarchical && enableHierarchicalSubAccountPositionsBlotter) ?? false;
  const includeCash = tab.includeCash ?? true;

  const columns = usePositionsSubAccountsColumns(defaultColumns);

  const persistedBlotterTable = usePersistedBlotterTable(blotterID, { filter: defaultFilter });

  // In order to be able to reference blotterTable.refreshClientSideRowModel before instanciating blotterTable,
  // we need to wrap it in a callback like this
  const refreshClientSideRowModel = useCallbackRef(() => {
    blotterTable.refreshClientSideRowModel();
  });

  // Here we take in the specs unique to the Row Group variant of the Sub account positions blotter.
  const rowGroupBlotterVariantSpecs = useRowGroupVariantSpecs();
  const hierarchicalBlotterVariantSpecs = useHierarchicalVariantSpecs({
    hierarchical,
    refreshClientSideRowModel: refreshClientSideRowModel,
  });

  const blotterSpecToUse = hierarchical ? hierarchicalBlotterVariantSpecs : rowGroupBlotterVariantSpecs;

  const filterResults = usePositionsSubAccountsFilter({
    persistedBlotterTable,
    defaultFilter,
    hierarchical,
  });

  const { filterBuilderProps } = filterResults;

  const updateShowZeroBalances = useCallbackRef((value: boolean) => {
    mixpanel.track(MixpanelEvent.ToggleShowZeroBalances);
    onUpdateTab({ ...tab, showZeroBalances: value });
  });

  const updateIncludeCash = useCallbackRef((value: boolean) => {
    mixpanel.track(MixpanelEvent.ToggleSubAccountPositionsIncludeCash);
    onUpdateTab({ ...tab, includeCash: value });
  });

  const { request: baseRequest, onStateUpdated: maybeModifyRequestOnStateUpdated } =
    useSubAccountPositionsBlotterRequest({
      initialGridState: persistedBlotterTable.initialState?.gridState,
      initialColumns: useConstant(columns),
      requestTag: blotterID,
      showZeroBalances,
      hierarchical,
    });
  const request = useMemo(
    () => ({
      ...baseRequest,
      ...removeEmptyFilters(filterResults.filter),
    }),
    [baseRequest, filterResults.filter]
  );

  const handleGridStateUpdated = useCallbackRef((event: StateUpdatedEvent) => {
    onGroupingStateUpdated(event);
    // Check the PnLLookbacks and see if we should amend the request based on any new visible columns
    maybeModifyRequestOnStateUpdated(event);
  });

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: initialIsOpen },
    filterBuilderProps,
  });

  const positionsMenu = usePositionsMenu({
    openClause: filterBuilderAccordion.openClause,
    filterableProperties: filterBuilderProps.properties,
    tabType: 'Sub Accounts',
    getColIdForFiltering: hierarchical ? treeBlotterContextMenuColumnResolver : undefined,
    showOpenTransactionsMenuItem: true,
  });

  const columnsWithMenu = useMemo(() => [...columns, ...positionsMenu.columns], [columns, positionsMenu.columns]);

  const includeCashFilter = useIncludeCashFilterFunc({
    includeCash,
    assetGetter: getPositionAsset,
  });

  const filterPipe = useWSFilterPipe({ getUniqueKey: getPositionKey, filterFunc: includeCashFilter });

  const blotterTable = useWsBlotterTable<WebsocketRequest, Position>({
    request,
    rowID: 'rowID' satisfies keyof Position,
    pipe: filterPipe,
    persistence: persistedBlotterTable,
    columns: columnsWithMenu,
    gridOptions: {
      ...blotterSpecToUse.gridOptionsOverlay,
      onStateUpdated: handleGridStateUpdated,
      rowSelection: DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
      getContextMenuItems: positionsMenu.getContextMenuItems,
    },
    exportOptions: {
      name: 'SubAccounts',
      tabLabel: tab.label,
    },
  });

  const { expandAllGroups, collapseAllGroups, setRowGroupColumns } = blotterTable;

  const { onStateUpdated: onGroupingStateUpdated, groupingToggleButtons } = useBlotterGroupingToggling({
    blotterName: 'sub account positions',
    setRowGroupColumns,
    groupings: rowGroupBlotterVariantSpecs.groupings,
    allowNoGrouping: true,
  });

  const handleCloneTab = useCallbackRef(() => {
    onCloneTab?.();
  });

  const extrasMenuPopover = useBlotterTableExtrasMenu();

  const updateHierarchicalSetting = useCallbackRef((value: boolean) => {
    mixpanel.track(MixpanelEvent.ToggleSubAccountPositionsBlotterHierarchical, {
      [MixpanelEventProperty.Checked]: value,
    });
    onUpdateTab({ ...tab, hierarchical: value });
  });

  return (
    <>
      <BlotterTableFilters
        {...filterBuilderAccordion}
        {...blotterTable.blotterTableFiltersProps}
        portalId={BLOTTER_TABLE_FILTERS_CONTAINER_ID}
        accordionBodyPrefix={!hierarchical ? groupingToggleButtons : null}
        prefix={
          <>
            <IconButton
              icon={IconName.ListExpand}
              size={FormControlSizes.Small}
              variant={ButtonVariants.Default}
              onClick={expandAllGroups}
              data-testid="expand-all-button"
            />
            <IconButton
              icon={IconName.ListCollapse}
              size={FormControlSizes.Small}
              variant={ButtonVariants.Default}
              onClick={collapseAllGroups}
            />
          </>
        }
        suffix={
          <>
            {/* If our hierarchy is proven to not be a tree, and we are showing the hierarchical blotter, then warn the user. */}
            {hierarchical && <SubAccountTreeWarning />}
            <BlotterTableExtrasMenu {...extrasMenuPopover}>
              <Toggle
                tooltip={CONTROL_TOOLTIPS.showZeroBalances}
                tooltipProps={EXTRAS_MENU_TOOLTIP_PROPS}
                size={FormControlSizes.Small}
                checked={showZeroBalances}
                onChange={updateShowZeroBalances}
                label="Show Zero Balances"
              />
              <Toggle
                tooltip={CONTROL_TOOLTIPS.includeCash}
                tooltipProps={EXTRAS_MENU_TOOLTIP_PROPS}
                size={FormControlSizes.Small}
                checked={includeCash}
                onChange={updateIncludeCash}
                label="Include Cash"
              />
              {enableHierarchicalSubAccountPositionsBlotter && (
                <Toggle
                  tooltip={CONTROL_TOOLTIPS.showRollupHierarchy}
                  tooltipProps={EXTRAS_MENU_TOOLTIP_PROPS}
                  size={FormControlSizes.Small}
                  checked={hierarchical}
                  onChange={updateHierarchicalSetting}
                  label="Show Rollup Hierarchy"
                />
              )}
              <Divider orientation="horizontal" />
              <Button
                startIcon={IconName.Duplicate}
                variant={ButtonVariants.Default}
                size={FormControlSizes.Small}
                onClick={handleCloneTab}
                data-testid="clone-tab-button"
              >
                Clone Tab
              </Button>

              <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />

              {isAuthorizedToEditPositions && (
                <Button
                  startIcon={IconName.Pencil}
                  variant={ButtonVariants.Default}
                  size={FormControlSizes.Small}
                  onClick={() => positionsMenu.openUpdatePositionDialog()}
                >
                  Adjust Sub Account Position
                </Button>
              )}
            </BlotterTableExtrasMenu>
          </>
        }
      />
      <BlotterTable {...blotterTable} />
      {positionsMenu.dialogs}
    </>
  );
}
